import React, {useState} from "react";
import strapi from "/scripts/apis/strapi/index.js";
import marked from "/scripts/marked/index.js";
import css from "./index.module.scss";
import DefaultLayout from "layouts/default";
import ImageCard from "@/components/imageCard";
import Button from "@/components/button";
import EnterpriseSvg from "/public/images/icons/enterprise.svg";
import EyeSvg from "/public/images/icons/eye.svg";
import Tag from "@/components/tag/index.jsx";
import { StrapiPicture } from "@/components/picture";
import { addToCart, sendDatalayerEvent } from "@/scripts/utils";
import ProductCard from "@/components/productCard";
import { useEffect } from "react";
import { useUser } from "@/scripts/apis/site/useUser";
import { useRouter } from "next/router";

function isFree(listing, user){
  return user?.isAdmin || (!listing.stripeProductId && listing.price === "0.00");
}

export default function ListingPage({listing, featuredListings}) {
  const user = useUser({refreshInterval: 10000}, {
    populate: {
      purchases: {
        fields: ["id", "createdAt"],
        populate: {
          listing: true,
        },
      },
    },
  });
  const router = useRouter();

  const [inCart, setInCart] = useState(false);
  const [cta, setCta] = useState({label: "Add to Cart", disabled: false});
  function handleAddToCart() {
    //immediately unlock free listings
    if (isFree(listing, user)){
      //require user to sign in
      if (!user?.value?.id){
        router.push(`/auth/register?ref=${window.location.href}`);
      } else {
        sendDatalayerEvent("purchase", "DL - Purchase", {price: listing.price});
        setCta({label: "Unlocking...", disabled: true});
        document.activeElement.blur();

        strapi.query(strapi.endpoints.PURCHASES_ME_CREATE, {
          data: {
            listing: listing.id,
          },
        }).then((res) => { //redirect to purchase page
          if (res.id){
            router.push(`/purchases?id=${res.id}`);
          } else {
            setCta({label: "Error!", disabled: false});
            console.error(res);
          }
        });
      }
    } else {
      //add non free listings to the cart
      sendDatalayerEvent("addToCart", "DL - Add to Cart");
      addToCart(listing);
      setCta({label: "Added!", disabled: true});
      setInCart(true);
      document.activeElement.blur();
    }
  }

  useEffect(() => {
    if (isFree(listing, user?.value)){
      setCta({label: "Unlock for Free", disabled: false});
    }
  }, [listing, user.value]);


  let purchasedOn = null;
  for (let purchase of user?.value?.purchases || []){
    if (purchase.listing.id === listing.id){
      purchasedOn = purchase.createdAt;
    }
  }

  let [showExamples, setShowExamples] = useState(false);

  return <DefaultLayout
    title={`${listing.name} | Lazy Prompts`}
    description={listing.shortDescription}
    mainClassName={css.main}
  >
    <section className={`light ${css.listing}`}>
      <div className="sectionInner">
        <ImageCard
          strapiPicture={listing.coverArt}
          overlaid
          light
          className={css.coverArt}
          sizes="(max-width: 1400px) 800px, 100vw"
        />
        <div className={css.description}>
          <h3>Product Description</h3>
          <div className={`markdown ${css.about}`} dangerouslySetInnerHTML={{__html: marked(listing.description || "Lorem ipsum...")}}></div>
        </div>
        <div className={css.title}>
          <div className={css.name}>{listing.business ? <EnterpriseSvg/> : undefined}{listing.name}</div>
          <a className={`custom ${css.author}`} href={`/stores/${listing?.owner?.id}`}>@{listing?.owner?.username}</a>
          <div className={`light ${css.tags}`}>
            {(listing.tags || []).map((tag) => <Tag
              key={tag.name}
              light
              label={tag.name}
              href={`/listings?aiModel=&query=&business=&tags=${encodeURIComponent(`[{"label":"${tag.name}","value":${ tag.id }}]`)}`}
            />)}
          </div>
        </div>

        <div className={css.sidebar}>
          <div className={css.section}>
            <div className={css.label}>Information</div>
            <div className={css.info}>Last updated: {(new Date(listing.updatedAt)).toLocaleDateString()}</div>
            {purchasedOn && <div className={css.info}>Last purchased: {(new Date(purchasedOn)).toLocaleDateString()}</div>}

          </div>

          <div className={css.section}>
            <div className={css.label}>AI Model</div>
            <div
              className={"light markdown"}
              dangerouslySetInnerHTML={{
                __html: marked(`This prompt is for <a href='${listing?.aiModel?.site}' target='_blank'>${listing?.aiModel?.name} ${listing?.aiModel?.version}</a>. ${listing?.aiModel?.information}\n\nAccess to ${listing?.aiModel?.name} is not included with your purchase.`),
              }}>
            </div>
          </div>

          <div className={css.price}>${listing.price}</div>

          <div className={css.buttons}>
            <Button
              label={cta.label}
              disabled={cta.disabled}
              onClick={handleAddToCart}
              notice={purchasedOn ? `Last purchased ${(new Date(purchasedOn)).toLocaleDateString()}` : ""}
            />
            {inCart && <Button
              label="View Cart"
              href="/account/cart"
            />}
          </div>
        </div>
      </div>
    </section>
    <section className={`light ${css.examples}`}>
      <div className="sectionInner">
        <h2>Example Outputs</h2>
        {listing.exampleImages && <div className={css.exampleImages}>
          {(listing?.exampleImages || []).map((example) => <ImageCard
            key={example.id}
            strapiPicture={example}
            overlaid
            light
            sizes="(max-width: 1300px) 50vw, (max-width:650px) 100vw, 25vw"
          />)}
        </div>}

        {listing.exampleText && <div className={css.exampleTexts}>
          {listing.exampleText.split("\n\n\n").map((example) => <pre
            className={css.exampleCard}
            key={example.replace(/\n/g, "")}
          >
            <code>{example.trim()}</code>
            <button className={`${css.hider} ${showExamples ? css.hidden : ""}`} onClick={() => setShowExamples(true)}>
              <div className={css.background}><EyeSvg/></div>
              <div className={css.instruction}>Click to Reveal</div>
            </button>
          </pre>)}

        </div>}
        <div className={css.buttons}>
          <Button
            label={cta.label}
            disabled={cta.disabled}
            onClick={handleAddToCart}
            notice={purchasedOn ? `Last purchased ${(new Date(purchasedOn)).toLocaleDateString()}` : ""}
          />
          {inCart && <Button
            label="View Cart"
            href="/account/cart"
          />}
        </div>
      </div>
    </section>
    {listing?.owner && <section className={`${css.author}`}>
      <div className="sectionInner">
        <h2>About the Engineer</h2>
        <a className={`custom ${css.username}`} href="/">
          <div className={css.profilePicture}>
            <StrapiPicture
              picture={listing?.owner?.profilePicture}
              alt = "Profile Picture"
              loading = "lazy"
              sizes = "200px"
              backgroundColor="var(--white)"
            />
          </div>
          <div className={css.text}>
            <div className={css.username}>@{listing?.owner?.username}</div>
            <div className={css.createdAt}>Joined on {(new Date(listing?.owner?.createdAt)).toLocaleDateString()}.</div>
          </div>
        </a>
        {listing?.owner?.biography && <div className={`markdown ${css.markdown}`} dangerouslySetInnerHTML={{
          __html: marked(listing?.owner?.biography),
        }}></div>}

      </div>
    </section>}

    <section className={`light ${css.featured}`}>
      <div className="sectionInner">
        <h2>Featured Prompts</h2>
        <div className={css.prompts}>
          {featuredListings.map((featuredListing, idx) => <ProductCard
            light
            key={featuredListing.id}
            listing={featuredListing}
            sizes="(max-width: 1500px) 50vw, 500px"
            overlaid={idx === 0}
          />)}
        </div>
      </div>
    </section>

  </DefaultLayout>;
}

//one path for each session
export async function getStaticPaths() {
  let listings = (await strapi.query(strapi.endpoints.LISTINGS_FIND, {
    fields: ["slug"],
  }));

  return {
    paths: listings.map((listing) => {
      return {params: {listing: listing.slug}};
    }),
    fallback: "blocking",
  };
}
export async function getStaticProps(query) {
  let listing = (await strapi.query(strapi.endpoints.LISTINGS_FIND, {
    filters: {
      slug: {
        $eq: query.params.listing,
      },
    },
    populate: {
      coverArt: "*",
      exampleImages: "*",
      aiModel: {
        fields: ["name", "version", "information", "tag", "site"],
      },
      owner: {
        populate: {
          profilePicture: true,
        },
      },
      tags: {
        fields: ["name"],
      },
    },
    publicationState: query.preview ? "preview" : "live",
  }))[0];

  if (!listing){
    return {
      redirect: {
        destination: "/404",
        permanent: false,
      },
      revalidate: 60,
    };
  }


  let featuredListings = await strapi.query(strapi.endpoints.LISTINGS_FIND, {
    data: {
      id: 17,
    },
    filters: {
      featured: true,
    },
    populate: {
      coverArt: true,
      aiModel: true,
    },
    pagination: {
      pageSize: 5,
    },
  });


  return {
    props: {
      listing,
      featuredListings,
    },
    revalidate: 60,
  };
}
